import React, { useEffect, useMemo, useReducer, useState } from "react";
import PageContainer from "../../components/PageContainer";
import MainContent from "../../components/MainContent";
import PageTitle from "../../components/Common/PageTitle/PageTitle";
import { useVendor } from "../../DataStore/VendorContext";
import ajaxPayee from "../../util/remote/ajaxPayee";
import Notification from "../../components/Common/Notification";
import ActivityLoader from "../../components/Common/ActivityLoader";
import TextInput from "../../components/Common/TextInput";
import CallOut from "../../components/Common/CallOut";
import SectionTitle from "../../components/Common/SectionTitle";

const initialState = {
  loading: false,
  vendorName: "",
  description: "",
  address: "",
  phoneNumber: "",
  emailAddress: "",
};

const reducer = (state, action) => {
  if (action.type === "RESET") return { ...initialState };
  return { ...state, [action.type]: action.value };
};

// 🔹 helper: consistent responsive columns (1 / 2 / 3 per row)
const Col = ({ children }) => <div className="col-12 col-md-6 col-xl-4">{children}</div>;

const NewVendorPage = () => {
  const { getVendorListing } = useVendor();
  const [currentNotify, setNotify] = useState(null);
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    if (!currentNotify) return;
    const t = setTimeout(() => setNotify(null), 5000);
    return () => clearTimeout(t);
  }, [currentNotify]);

  const calloutDesc = useMemo(() => {
    const n = (state.vendorName || "").trim();
    return n
      ? `Creating vendor: ${n}`
      : "Add new vendor for your transaction payees and funds sources.";
  }, [state.vendorName]);

  const handleSave = async (event) => {
    event.preventDefault();
    if (state.loading) return;

    if (!String(state.vendorName || "").trim()) {
      setNotify(<Notification type="danger" message="Enter Vendor Name and try again." />);
      return;
    }

    dispatch({ type: "loading", value: true });

    const server_response = await ajaxPayee.addVendor(
      state.vendorName,
      state.description,
      state.address,
      state.phoneNumber,
      state.emailAddress
    );

    dispatch({ type: "loading", value: false });

    if (server_response?.status === "OK") {
      dispatch({ type: "RESET" });

      // Refresh vendor listing shortly after success feedback
      setTimeout(() => {
        getVendorListing(true);
      }, 2000);

      setNotify(<Notification type="success" message={server_response.message} />);
    } else {
      setNotify(<Notification type="danger" message={server_response?.message || "Failed to save vendor."} />);
    }
  };

  return (
    <PageContainer>
      <MainContent>
        <PageTitle
          title="New Vendor"
          description="Create new system vendor also known as payee."
          iconClass="fa fa-truck"
        />

        <div className="card border-0 shadow-sm mb-3">
          <div className="card-body">
            {currentNotify}

            <form method="post" onSubmit={handleSave}>
              {/* ---------- BASIC INFO ---------- */}
              <div className="row mb-2">
                <div className="col-12">
                  <SectionTitle
                    icon="fa fa-user"
                    title="Basic Info"
                    note="Vendor name and description help with reporting and searching."
                  />
                </div>
              </div>

              <div className="row" style={{ rowGap: 12 }}>
                <div className="col-12 col-xl-6">
                  <TextInput
                    type="text"
                    label="Vendor Name"
                    placeholder="Vendor Name"
                    leftIcon="fa fa-user"
                    value={state.vendorName}
                    onChange={(e) => dispatch({ type: "vendorName", value: e.target.value })}
                    required
                    wrap
                  />
                </div>

                <div className="col-12">
                  <TextInput
                    type="textarea"
                    label="Business Description"
                    placeholder="What does this vendor supply? (Optional but helpful)"
                    leftIcon="fa fa-list"
                    value={state.description}
                    onChange={(e) => dispatch({ type: "description", value: e.target.value })}
                    wrap
                  />
                </div>
              </div>

              {/* ---------- CONTACT DETAILS ---------- */}
              <div className="row mt-3 mb-2">
                <div className="col-12">
                  <SectionTitle
                    icon="fa fa-address-card"
                    title="Contact Details"
                    note="Optional: add contact details for invoices and follow-ups."
                  />
                </div>
              </div>

              <div className="row" style={{ rowGap: 12 }}>
                <Col>
                  <TextInput
                    type="text"
                    label="Vendor Address"
                    placeholder="Vendor Address"
                    leftIcon="fa fa-map-pin"
                    value={state.address}
                    onChange={(e) => dispatch({ type: "address", value: e.target.value })}
                    wrap
                  />
                </Col>

                <Col>
                  <TextInput
                    type="text"
                    label="Phone Number"
                    placeholder="Phone Number"
                    leftIcon="fa fa-phone"
                    value={state.phoneNumber}
                    onChange={(e) => dispatch({ type: "phoneNumber", value: e.target.value })}
                    wrap
                  />
                </Col>

                <Col>
                  <TextInput
                    type="email"
                    label="Email Address"
                    placeholder="Email Address"
                    leftIcon="fa fa-envelope"
                    value={state.emailAddress}
                    onChange={(e) => dispatch({ type: "emailAddress", value: e.target.value })}
                    wrap
                  />
                </Col>
              </div>

              {/* Actions */}
              <div className="d-flex flex-column flex-md-row justify-content-end mt-3" style={{ gap: 10 }}>
                {!state.loading ? (
                  <button type="submit" className="btn btn-lg btn-success">
                    <i className="fa fa-check-circle mr-2" />
                    Save Vendor
                  </button>
                ) : (
                  <div className="d-flex align-items-center">
                    <ActivityLoader />
                  </div>
                )}

                <button
                  type="button"
                  className="btn btn-lg btn-light"
                  onClick={() => dispatch({ type: "RESET" })}
                  disabled={state.loading}
                >
                  <i className="fa fa-undo mr-2" />
                  Reset
                </button>
              </div>

              <div className="text-muted mt-2" style={{ fontSize: 12 }}>
                Tip: Add a descriptive name (e.g. “National Water - Main Branch”) to improve search and reporting.
              </div>
            </form>
          </div>
        </div>
      </MainContent>
    </PageContainer>
  );
};

export default NewVendorPage;
